// src/manager/pages/CreateTable.tsx — UPDATED RTP RANGE 50-150%
// Manager can now set RTP from 50% to 150% for operator grade flexibility

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useManager } from '../hooks/useManager';
import { toast } from 'react-hot-toast';

interface FormData {
  name: string;
  location: string;
  password: string;
  runtimeProfile: 'pc' | 'casino';
  rtp: number;
  payoutRatio: number;
}

interface CreatedTableSummary {
  id: string;
  name: string;
  location: string | null;
  runtimeProfile: 'pc' | 'casino';
  tablePassword: string;
  launchUrl: string;
}

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;

export default function CreateTable() {
  const { createTable, fetchTables, socket } = useManager();
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<FormData>({
    defaultValues: {
      rtp: 95,           // Balanced default for operator flexibility
      payoutRatio: 1,
      runtimeProfile: 'pc',
      location: '',
      password: '',
      name: '',
    },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdTable, setCreatedTable] = useState<CreatedTableSummary | null>(null);

  const currentRtp = watch('rtp', 95);
  const runtimeProfile = watch('runtimeProfile', 'pc');

  // Real-time table created listener
  useEffect(() => {
    if (!socket) return;

    const handleTableCreated = (data: { name: string }) => {
      toast.success(`New table created: ${data.name}`);
      fetchTables(); // Refresh list
    };

    socket.on('table-created', handleTableCreated);

    return () => {
      socket.off('table-created', handleTableCreated);
    };
  }, [socket, fetchTables]);

  const onSubmit = async (data: FormData) => {
    setError('');
    setLoading(true);

    if (data.rtp < RTP_MIN_PERCENT || data.rtp > RTP_MAX_PERCENT) {
      setError(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}%`);
      toast.error(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}%`);
      setLoading(false);
      return;
    }

    try {
      const location = data.location.trim();
      const result = await createTable({
        name: DOMPurify.sanitize(data.name.trim()),
        location: location ? DOMPurify.sanitize(location) : null,
        password: data.password,
        runtimeProfile: data.runtimeProfile,
        rtp: Number(data.rtp),
        payout_ratio: Number(data.payoutRatio),
      });

      const launchParams = new URLSearchParams({
        tableId: result.id,
        runtime: data.runtimeProfile,
        viewMode: data.runtimeProfile === 'casino' ? 'shared' : 'personal',
      });

      setCreatedTable({
        id: result.id,
        name: data.name.trim(),
        location: location || null,
        runtimeProfile: data.runtimeProfile,
        tablePassword: result.tablePassword || data.password,
        launchUrl: `/game?${launchParams.toString()}`,
      });

      await fetchTables();
      reset();
      toast.success('Table created successfully!');
    } catch (err: any) {
      const msg = err.message || 'Failed to create table';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Create New Table</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Table Name
            </label>
            <input
              id="name"
              placeholder="VIP Table 1"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              {...register('name', {
                required: 'Table name required',
                pattern: { value: /^[A-Za-z0-9\s]{2,50}$/, message: '2-50 characters, letters, numbers, spaces only' },
              })}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location (Optional)
            </label>
            <input
              id="location"
              placeholder="Mumbai Branch"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              {...register('location', {
                pattern: { value: /^[A-Za-z0-9\s]{0,100}$/, message: '0-100 characters, letters, numbers, spaces only' },
              })}
            />
            {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>}
          </div>

          <div className="relative">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Room Password
            </label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="At least 6 characters"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 pr-12"
              {...register('password', {
                required: 'Password required',
                minLength: { value: 6, message: 'Password must be 6-50 characters' },
                maxLength: { value: 50, message: 'Password must be 6-50 characters' },
              })}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-10 text-gray-600 hover:text-gray-800"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Runtime Setup
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className={`rounded-lg border px-4 py-3 cursor-pointer transition ${runtimeProfile === 'pc' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 bg-white'}`}>
                <input type="radio" value="pc" className="sr-only" {...register('runtimeProfile', { required: true })} />
                <div className="text-sm font-semibold text-gray-900">PC Table</div>
                <div className="mt-1 text-xs text-gray-600">Personal bottom-center style view</div>
              </label>
              <label className={`rounded-lg border px-4 py-3 cursor-pointer transition ${runtimeProfile === 'casino' ? 'border-purple-600 bg-purple-50' : 'border-gray-300 bg-white'}`}>
                <input type="radio" value="casino" className="sr-only" {...register('runtimeProfile', { required: true })} />
                <div className="text-sm font-semibold text-gray-900">Casino Table</div>
                <div className="mt-1 text-xs text-gray-600">Shared 6-seat table layout</div>
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="rtp" className="block text-sm font-medium text-gray-700 mb-1">
              RTP (%) - Manager Range: 50-150
            </label>
            <div className="relative">
              <input
                id="rtp"
                type="range"
                min={RTP_MIN_PERCENT}
                max={RTP_MAX_PERCENT}
                step="0.1"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                {...register('rtp', {
                  required: 'RTP required',
                  min: { value: RTP_MIN_PERCENT, message: `Minimum ${RTP_MIN_PERCENT}%` },
                  max: { value: RTP_MAX_PERCENT, message: `Maximum ${RTP_MAX_PERCENT}%` },
                  valueAsNumber: true,
                })}
              />
              <div className="text-center text-lg font-bold mt-2 text-purple-700">
                {currentRtp.toFixed(1)}%
              </div>
            </div>
            {errors.rtp && <p className="mt-1 text-sm text-red-600">{errors.rtp.message}</p>}
          </div>

          <div>
            <label htmlFor="payoutRatio" className="block text-sm font-medium text-gray-700 mb-1">
              Payout Ratio (Credits per Dollar)
            </label>
            <input
              id="payoutRatio"
              type="number"
              step="0.01"
              placeholder="Enter payout ratio"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
              {...register('payoutRatio', {
                required: 'Payout ratio required',
                min: { value: 0.01, message: 'Min 0.01' },
                max: { value: 100, message: 'Max 100' },
                valueAsNumber: true,
              })}
            />
            {errors.payoutRatio && <p className="mt-1 text-sm text-red-600">{errors.payoutRatio.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-6 font-medium rounded-lg text-white transition shadow ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="animate-spin mr-2" size={20} />
                Creating...
              </span>
            ) : (
              'Create Table'
            )}
          </button>

          <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-blue-900">
            Launch URL:
            <div className="mt-1 font-mono break-all">
              {`/game?runtime=${runtimeProfile}${runtimeProfile === 'casino' ? '&viewMode=shared' : '&viewMode=personal'}`}
            </div>
          </div>
        </form>
      </div>

      {createdTable && (
        <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-950 shadow-sm">
          <h3 className="text-lg font-semibold">Table Created</h3>
          <div className="mt-3 space-y-2 text-sm">
            <div><span className="font-medium">Table Name:</span> {createdTable.name}</div>
            <div><span className="font-medium">Table ID:</span> <span className="font-mono break-all">{createdTable.id}</span></div>
            <div><span className="font-medium">Table Password:</span> <span className="font-mono break-all">{createdTable.tablePassword}</span></div>
            <div><span className="font-medium">Runtime:</span> {createdTable.runtimeProfile === 'casino' ? 'Casino Table' : 'PC Table'}</div>
            <div><span className="font-medium">Location:</span> {createdTable.location || 'Not set'}</div>
            <div>
              <span className="font-medium">Launch URL:</span>
              <div className="mt-1 font-mono break-all">{createdTable.launchUrl}</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-white/70 px-3 py-2 text-xs">
              Room access ke liye `Table ID` use hoga. Agar room password set hai, join karte waqt wahi password bhi chahiye hoga.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
